import { FC, useMemo, useRef, useState } from 'react';

import { IconButton } from '@rbilabs/universal-components';
import { FlatList, LayoutChangeEvent, StyleSheet, View } from 'react-native';

import {
  CardContainerWrapper,
  Container,
  LeftButtonWrapper,
  MeasureBox,
  RightButtonWrapper,
} from 'components/responsive-carousel/responsive-carousel.styled.web';
import { Sizes } from 'components/responsive-carousel/types';
import { ILoyaltyBadge } from 'pages/loyalty/loyalty-badges/types';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import { BADGE_CARD_WIDTH, BadgeCard } from './badge-card';

const BADGE_CARD_MARGIN = 2;
const BADGE_LAYOUT_WIDTH = BADGE_CARD_WIDTH + BADGE_CARD_MARGIN * 2;
const BADGE_ITEM_STRIDE = BADGE_LAYOUT_WIDTH + Sizes.SPACER;

const BadgeSeparator = () => <View style={styles.badgeSeparator} />;

interface IBadgesSectionCarouselProps {
  badges: ILoyaltyBadge[];
  onBadgePress: (badgeId: string) => void;
}

export const BadgesSectionCarousel: FC<IBadgesSectionCarouselProps> = ({
  badges,
  onBadgePress,
}) => {
  const flatListRef = useRef<FlatList<ILoyaltyBadge>>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [carouselWidth, setCarouselWidth] = useState(0);

  const cardsPerPage = Math.max(1, Math.floor(carouselWidth / BADGE_CARD_WIDTH));
  const maxStartIndex = Math.max(0, badges.length - cardsPerPage);
  const disableRightButton = startIndex >= maxStartIndex;
  const cardContainerWidth = (BADGE_CARD_WIDTH + Sizes.SPACER) * cardsPerPage;

  const showMore = () => {
    const nextIndex = Math.min(startIndex + cardsPerPage, maxStartIndex);
    setStartIndex(nextIndex);
    flatListRef.current?.scrollToIndex({ animated: true, index: nextIndex });
  };

  const showLess = () => {
    const prevIndex = Math.max(0, startIndex - cardsPerPage);
    setStartIndex(prevIndex);
    flatListRef.current?.scrollToIndex({ animated: true, index: prevIndex });
  };

  const onLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    setCarouselWidth(nativeEvent.layout.width);
  };

  const getItemLayout = useMemo(
    () => (_: ArrayLike<ILoyaltyBadge> | null | undefined, index: number) => ({
      length: BADGE_LAYOUT_WIDTH,
      offset: BADGE_ITEM_STRIDE * index,
      index,
    }),
    []
  );

  return (
    <Container bgColor="transparent" justifyContent="center" testID="carousel">
      <LeftButtonWrapper bgColor="transparent">
        <IconButton
          isDisabled={startIndex === 0}
          variant="outline"
          iconName="back"
          onPress={showLess}
          {...hiddenAccessibilityPlatformProps}
        />
      </LeftButtonWrapper>

      <MeasureBox onLayout={onLayout}>
        <CardContainerWrapper width={cardContainerWidth}>
          <FlatList
            ref={flatListRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            scrollEnabled={false}
            data={badges}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <BadgeCard
                badge={item}
                onPress={() => onBadgePress(item.id)}
                accessibilityRole="link"
              />
            )}
            ItemSeparatorComponent={BadgeSeparator}
            getItemLayout={getItemLayout}
          />
        </CardContainerWrapper>
      </MeasureBox>

      <RightButtonWrapper bgColor="transparent">
        <IconButton
          variant="outline"
          iconName="forward"
          onPress={showMore}
          isDisabled={disableRightButton}
          {...hiddenAccessibilityPlatformProps}
        />
      </RightButtonWrapper>
    </Container>
  );
};

const styles = StyleSheet.create({
  badgeSeparator: {
    marginRight: Sizes.SPACER,
  },
});
